import React, { useState, useEffect, useCallback } from 'react';
import { api } from '@/api';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { BookOpen, Plus, FileText, Calendar, Trash2 } from 'lucide-react';

const SUB_TYPES = ['referral', 'response', 'statement_of_case', 'replication', 'reply', 'expert_report', 'witness_statement', 'skeleton_argument', 'closing_submission'];
const STATUS_COLORS = {
  draft: 'bg-slate-100 text-slate-600', under_review: 'bg-amber-100 text-amber-700',
  filed: 'bg-green-100 text-green-700', accepted: 'bg-blue-100 text-blue-700',
  rejected: 'bg-red-100 text-red-700',
};

export default function SubmissionsWorkspace({ matterId, issues, evidence }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ submission_type: 'referral', title: '', description: '', deadline_date: '', notes: '' });

  const loadData = useCallback(async () => {
    try {
      const res = await api.disputes.listSubmissions(matterId);
      setSubmissions(res.data || []);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAdd = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      await api.disputes.createSubmission(matterId, { ...form, matter_id: matterId });
      toast.success('Submission created');
      setShowAdd(false);
      setForm({ submission_type: 'referral', title: '', description: '', deadline_date: '', notes: '' });
      loadData();
    } catch { toast.error('Failed to create submission'); }
  };

  const handleStatusUpdate = async (subId, status) => {
    try {
      await api.disputes.updateSubmission(subId, { status });
      toast.success('Status updated');
      loadData();
    } catch { toast.error('Update failed'); }
  };

  const handleDelete = async (id) => {
    try { await api.disputes.deleteSubmission(id); toast.success('Deleted'); loadData(); } catch { toast.error('Delete failed'); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-2 border-slate-300 border-t-slate-800 rounded-full" /></div>;

  const grouped = { draft: [], under_review: [], filed: [], accepted: [], rejected: [] };
  submissions.forEach(s => { (grouped[s.status] || grouped.draft).push(s); });

  return (
    <div className="space-y-4" data-testid="submissions-workspace">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-700">Submissions ({submissions.length})</h3>
        <Button size="sm" className="bg-slate-900 hover:bg-slate-800 gap-1 text-xs" onClick={() => setShowAdd(true)} data-testid="add-submission-btn"><Plus className="h-3 w-3" /> New Submission</Button>
      </div>

      {/* Status Summary */}
      <div className="grid grid-cols-5 gap-2">
        {Object.entries(grouped).map(([status, items]) => (
          <Card key={status}><CardContent className="p-2 text-center">
            <p className="text-lg font-bold">{items.length}</p>
            <p className="text-[9px] uppercase text-slate-500">{status.replace(/_/g, ' ')}</p>
          </CardContent></Card>
        ))}
      </div>

      {submissions.length === 0 ? (
        <Card><CardContent className="p-8 text-center"><BookOpen className="h-10 w-10 text-slate-300 mx-auto mb-2" /><p className="text-sm text-slate-500">No submissions yet</p></CardContent></Card>
      ) : (
        <div className="space-y-2">
          {submissions.map(s => (
            <Card key={s.id} data-testid={`submission-${s.id}`}>
              <CardContent className="p-3">
                <div className="flex items-start gap-3">
                  <FileText className="h-5 w-5 text-slate-400 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="font-mono text-[10px] text-slate-400">{s.submission_ref}</span>
                      <Badge className={`text-[10px] ${STATUS_COLORS[s.status]}`}>{s.status?.replace(/_/g, ' ')}</Badge>
                      <Badge variant="outline" className="text-[10px] capitalize">{s.submission_type?.replace(/_/g, ' ')}</Badge>
                    </div>
                    <h4 className="text-sm font-medium text-slate-800">{s.title}</h4>
                    {s.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{s.description}</p>}
                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                      {s.deadline_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" /> Due: {s.deadline_date}</span>}
                      {s.filed_date && <span>Filed: {s.filed_date}</span>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0 items-center">
                    {s.status === 'draft' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusUpdate(s.id, 'under_review')}>Submit</Button>}
                    {s.status === 'under_review' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusUpdate(s.id, 'filed')}>File</Button>}
                    {s.status === 'filed' && <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => handleStatusUpdate(s.id, 'accepted')}>Accept</Button>}
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-slate-400 hover:text-red-500" onClick={() => handleDelete(s.id)}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="sm:max-w-md">
          <DialogTitle className="font-barlow font-bold uppercase">New Submission</DialogTitle>
          <div className="space-y-3 mt-2">
            <div><Label className="text-xs">Submission Type *</Label>
              <Select value={form.submission_type} onValueChange={v => setForm(f => ({...f, submission_type: v}))}>
                <SelectTrigger data-testid="submission-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{SUB_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Title *</Label><Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} placeholder="e.g. Referral Submission" data-testid="submission-title-input" /></div>
            <div><Label className="text-xs">Description</Label><Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={2} /></div>
            <div><Label className="text-xs">Deadline Date</Label><Input type="date" value={form.deadline_date} onChange={e => setForm(f => ({...f, deadline_date: e.target.value}))} /></div>
            <div><Label className="text-xs">Notes</Label><Textarea value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} rows={2} placeholder="Internal notes..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={handleAdd} data-testid="submit-submission"><Plus className="h-4 w-4 mr-1" /> Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
